import { Vector2 } from 'three';

export const calculatePolygonCentroid = (vertices: Vector2[]): Vector2 => {
    let signedArea = 0;
    let cx = 0, cy = 0;
  
    for (let i = 0; i < vertices.length; i++) {
      const j = (i + 1) % vertices.length;
      const a = vertices[i].x * vertices[j].y - vertices[j].x * vertices[i].y;
  
      signedArea += a;
      cx += (vertices[i].x + vertices[j].x) * a;
      cy += (vertices[i].y + vertices[j].y) * a;
    }
  
    signedArea *= 0.5;
    cx /= (6 * signedArea);
    cy /= (6 * signedArea);
  
    return new Vector2(cx, cy);
  };

  // Shoelace-based signed area and area-weighted polygon centroid (not the average of vertices,
  // which is skewed towards denser vertex clusters and does not represent the true center of mass).
  // Degenerate (zero-area / collinear) input falls back to the vertex average for the centroid.
  export const calculatePolygonAreaAndCentroid = (points2d: Vector2[]): { area: number, centroid: Vector2 } => {
    let signedArea = 0;
    let cx = 0;
    let cy = 0;

    for (let i = 0; i < points2d.length; i++) {
      const current = points2d[i];
      const next = points2d[(i + 1) % points2d.length];
      const cross = current.x * next.y - next.x * current.y;

      signedArea += cross;
      cx += (current.x + next.x) * cross;
      cy += (current.y + next.y) * cross;
    }

    signedArea /= 2;

    if (Math.abs(signedArea) < 1e-9) {
      const centroid = points2d.reduce((sum, p) => sum.add(p), new Vector2()).divideScalar(points2d.length);
      return { area: 0, centroid };
    }

    const centroid = new Vector2(cx, cy).divideScalar(6 * signedArea);

    return { area: Math.abs(signedArea), centroid };
  }

  // 2D convex hull via Andrew's monotone chain, O(n log n). Used to derive a true top-down
  // projection/silhouette of a 3D solid from its projected vertices (see
  // ExtrudedLoopAnnotationData.getTopDownProjectionPoints2d()) — for a convex profile extruded
  // along a straight line, the hull of the projected front+back face vertices is exactly the
  // solid's silhouette; for a concave profile it's an over-approximation, which is an accepted
  // simplification (exact concave silhouette projection along an arbitrary 3D direction is a much
  // harder problem than this needs to solve).
  export const convexHull2d = (points: Vector2[]): Vector2[] => {
    const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
    if (sorted.length < 3) return sorted;

    const cross = (o: Vector2, a: Vector2, b: Vector2): number =>
      (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

    const buildHalf = (pts: Vector2[]): Vector2[] => {
      const half: Vector2[] = [];
      for (const p of pts) {
        while (half.length >= 2 && cross(half[half.length - 2], half[half.length - 1], p) <= 0) {
          half.pop();
        }
        half.push(p);
      }
      return half;
    };

    const lower = buildHalf(sorted);
    const upper = buildHalf([...sorted].reverse());

    lower.pop();
    upper.pop();
    return lower.concat(upper);
  }

  export const isEqualWithinTolerance = (a: number, b: number, tolerance: number): boolean => {
    if (__DEBUG__) {
      if (tolerance < 0) {
        throw new Error("Tolerance must be non-negative.");
      }
      if (isNaN(a) || isNaN(b)) {
        throw new Error("Input values must be numbers.");
      }
    }

    return Math.abs(a - b) <= tolerance;
  }

  export const roundToPrecision = (value: number, precision: number): number => {
    if (__DEBUG__) {
      if (precision < 0) {
        throw new Error("Precision must be non-negative.");
      }
      if (isNaN(value)) {
        throw new Error("Value must be a number.");
      }
    }

    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
  }